import { useEffect, useMemo, useState } from 'react'
import type { FC } from 'react'

import { LoaderCircle } from '@skytech/ui'
import { StyledText } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LoadingError } from '@/components/LoadingError'
import Tabs from '@/components/Tabs'
import { ADMIN_ROLES, USER_ROLE, useAccess } from '@/hooks/access'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import assetLocationResources from '@/resources/asset-locations'
import { commonQueries } from '@/resources/queries'
import { APP_ROUTES } from '@/util/app-routes'

import { Assets } from './Assets'
import { Profile } from './Profile'

export const AssetLocationDetails: FC = () => {
  useAccess([...ADMIN_ROLES, USER_ROLE.IT_USER])

  const navigate = useNavigate()
  const translate = useTranslation()
  const params = useParams()

  const assetLocationId = Number(params.assetLocationId)

  if (isNaN(assetLocationId)) throw Error(translate('Employee ID is not a number!'))

  const { data, isError } = useQuery(commonQueries.assetLocationDetails(assetLocationId), () =>
    assetLocationResources.getDetails(assetLocationId)
  )

  const { data: assets, isLoading: isAssetsLoading } = useQuery(
    queries.adminAssetList(),
    adminResources.getAssetList
  )

  const employeeAssets = assets?.filter(asset => asset.costObjectId === assetLocationId)

  const assetLocationDetailsTabs = useMemo(
    () => [
      { value: 'profile', label: <Translation id="Profile" /> },
      {
        value: 'assets',
        label: (
          <>
            {<Translation id="Assets" />}{' '}
            {isAssetsLoading ? (
              <Translation id="Loading..." />
            ) : (
              <>
                {employeeAssets?.length === 0 ? (
                  <StyledText size="regular-plus" colorMode="gray" className="count-styling">
                    {employeeAssets?.length}
                  </StyledText>
                ) : (
                  <StyledText size="regular-plus" className="count-styling">
                    {employeeAssets?.length}
                  </StyledText>
                )}
              </>
            )}
          </>
        ),
      },
    ],
    [employeeAssets?.length, isAssetsLoading]
  )

  const [queryTab, setQueryTab] = useSearchParamsByKey('tab')
  const [tabFilter, setTabFilter] = useState(queryTab || assetLocationDetailsTabs[0].value)

  useEffect(() => {
    setTabFilter(queryTab || assetLocationDetailsTabs[0].value)
  }, [assetLocationDetailsTabs, queryTab])

  if (isError)
    return (
      <Container onSwipedLeft={() => navigate(APP_ROUTES.HARDWARE_ASSET_LOCATION_LIST)}>
        <LoadingError />
      </Container>
    )

  if (!data) {
    return (
      <Container onSwipedLeft={() => navigate(APP_ROUTES.HARDWARE_ASSET_LOCATION_LIST)}>
        <LoaderCircle />
      </Container>
    )
  }

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.HARDWARE_ASSET_LOCATION_LIST)}>
      <div className="employee-details-title">
        <div className="flex items-center">
          <Container.Header>{data.name}</Container.Header>
        </div>
        <h4 className="customer-name">{data.customer.name}</h4>
      </div>

      <Tabs
        selectedValue={tabFilter}
        tabs={assetLocationDetailsTabs}
        onClick={tabId => {
          setTabFilter(String(tabId))
          setQueryTab(String(tabId))
        }}
      />

      <div className="cost-object-details">
        {tabFilter === 'profile' && (
          <Profile
            assetLocationDetails={data}
            onGoToAssetTab={() => {
              setTabFilter('assets')
              setQueryTab('assets')
            }}
          />
        )}
        {tabFilter === 'assets' && <Assets />}
      </div>
    </Container>
  )
}
